import { ClockCircleOutlined } from "@ant-design/icons";
import { Alert, Flex, Typography } from "antd";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { FORGOTTEN_CHECKIN_THRESHOLD_HOURS } from "../../../common/constants/constants";
import { formatHours } from "../../../common/utils/formatHours";
import { buildUserDisplayNameById } from "../../../common/utils/userDisplayName";
import type { UserLimitedList } from "../../generated-axios-client";
import type { RootState } from "../../store";

// Read from the ticket's own time sessions rather than the work order's
// active_time_tracking count: the sessions are already loaded for this page, they
// name the technicians a manager needs to see, and the count is not exposed on the
// work-order detail endpoint at all.
export const ActiveSessionsBanner = () => {
  const timeSessions = useSelector((state: RootState) => state.ticket.timeSessions);
  const userList = useSelector(
    (state: RootState) => (state.userstate.list.results ?? []) as UserLimitedList[]
  );

  const openSessions = timeSessions?.filter((session) => !session.ended_at) ?? [];
  if (openSessions.length === 0) return null;

  const userDisplayNameById = buildUserDisplayNameById(userList);

  const checkedInTechnicians = openSessions.map((session) => {
    const elapsedHours = session.started_at
      ? dayjs().diff(dayjs(session.started_at), "minute") / 60
      : null;

    return {
      key: session.id ?? `${session.assignee}-${session.started_at}`,
      name:
        session.assignee != null
          ? (userDisplayNameById[session.assignee] ?? `User ${session.assignee}`)
          : "Unknown technician",
      checkedInAt: session.started_at
        ? dayjs(session.started_at).format("DD/MM/YYYY HH:mm")
        : "an unknown time",
      elapsedText: elapsedHours != null ? formatHours(elapsedHours) : "unknown",
      isForgotten:
        elapsedHours != null && elapsedHours >= FORGOTTEN_CHECKIN_THRESHOLD_HOURS,
    };
  });

  const anyForgotten = checkedInTechnicians.some(
    (technician) => technician.isForgotten
  );

  return (
    <Alert
      type={anyForgotten ? "error" : "info"}
      showIcon
      icon={<ClockCircleOutlined />}
      message={anyForgotten ? "Forgotten Check-In" : "Active Check-In"}
      description={
        <Flex vertical gap={2}>
          {checkedInTechnicians.map((technician) => (
            <Typography.Text key={technician.key}>
              <Typography.Text strong>{technician.name}</Typography.Text>
              {` — checked in ${technician.checkedInAt}, ${technician.elapsedText}`}
            </Typography.Text>
          ))}
        </Flex>
      }
    />
  );
};
